import { z } from "zod/v4";
import {
  lineServerApiDefinition,
  telegramApiDefinition,
  userApiDefinition,
} from "../../client/src/apiTyping.ts";
import { endpointExamples } from "./endpointExamples.ts";

const publicApiDefinition = {
  ...lineServerApiDefinition,
  ...telegramApiDefinition,
  ...userApiDefinition,
} as const;

type JsonSchema = {
  type?: string;
  description?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  anyOf?: JsonSchema[];
  allOf?: JsonSchema[];
};

export type EndpointDefinition = {
  name: string;
  method: "POST";
  endpoint: string;
  description: string;
  auth: string;
  requestBody: Record<
    string,
    { type: string; required?: boolean; description: string }
  >;
  responseBody: Record<string, { type: string; description: string }>;
  example: { request: unknown; response: unknown };
};

const jsonSchemaTypeName = (prop: JsonSchema): string => {
  if (prop.type) return prop.type;
  if (prop.anyOf) return prop.anyOf.map(jsonSchemaTypeName).join(" | ");
  return "unknown";
};

const buildRequestBody = (inputSchema: JsonSchema) =>
  Object.fromEntries(
    Object.entries(inputSchema.properties ?? {}).map(([field, prop]) => [
      field,
      {
        type: jsonSchemaTypeName(prop),
        required: inputSchema.required?.includes(field),
        description: prop.description ?? "",
      },
    ]),
  );

const extractResponseFields = (outputSchema: JsonSchema) => {
  const successVariant = outputSchema.anyOf?.[0];
  if (!successVariant) return {};
  const valueSchema = successVariant.allOf?.[1];
  if (!valueSchema?.properties) return {};
  return Object.fromEntries(
    Object.entries(valueSchema.properties).map(([field, prop]) => [
      field,
      { type: jsonSchemaTypeName(prop), description: prop.description ?? "" },
    ]),
  );
};

export const endpoints: EndpointDefinition[] = Object.entries(
  publicApiDefinition,
)
  .flatMap(
    ([name, ep]) => {
      const example = endpointExamples[name as keyof typeof endpointExamples];
      if (!example) return [];
      const inputSchema = z.toJSONSchema(ep.input) as JsonSchema;
      const outputSchema = z.toJSONSchema(ep.output) as JsonSchema;
      return [{
        name,
        method: "POST" as const,
        endpoint: `/${name}`,
        description: inputSchema.description ?? "",
        auth: "Required - Include token in request body",
        requestBody: buildRequestBody(inputSchema),
        responseBody: extractResponseFields(outputSchema),
        example,
      }];
    },
  );
